export const EARTH_RADIUS_KM = 6371.0088;
const toRadians = (degrees) => (degrees * Math.PI) / 180;
const toDegrees = (radians) => (radians * 180) / Math.PI;
export function distanceKm(a, b) {
    const dLat = toRadians(b.lat - a.lat);
    const dLng = toRadians(b.lng - a.lng);
    const lat1 = toRadians(a.lat);
    const lat2 = toRadians(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}
/**
 * The course to steer from one point to another, degrees clockwise from north.
 *
 * Two coincident points have no course, which is reported as 0 rather than NaN: a marker
 * that cannot be placed is still drawn at the point it is at.
 */
export function legBearingDegrees(from, to) {
    const lat1 = toRadians(from.lat);
    const lat2 = toRadians(to.lat);
    const dLng = toRadians(to.lng - from.lng);
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    if (y === 0 && x === 0)
        return 0;
    return (toDegrees(Math.atan2(y, x)) + 360) % 360;
}
/**
 * How far either side of a sample the drawn bow's own course is read from, as a fraction of the
 * route. Small enough that the two points are the same place to a map, wide enough to carry a
 * real direction once the bow is tens of kilometres off the chord.
 */
const BOW_TANGENT_STEP = 1e-3;
/** Below this the two endpoints define no plane worth bowing in. */
const BOW_AXIS_MIN_LENGTH = 1e-9;
const unitVector = (lat, lng) => ({
    x: Math.cos(lat) * Math.cos(lng),
    y: Math.cos(lat) * Math.sin(lng),
    z: Math.sin(lat),
});
const clampUnit = (value) => Math.min(1, Math.max(-1, value));
/** Back to degrees, longitude wrapped where a map reads it. */
const pointOf = (vector) => ({
    lat: toDegrees(Math.asin(clampUnit(vector.z))),
    lng: ((toDegrees(Math.atan2(vector.y, vector.x)) + 540) % 360) - 180,
});
/** Rotate a point out of the plane it lies in, by `angle` radians about the plane's own normal. */
const outOfPlane = (point, normal, angle) => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
        x: point.x * cos + normal.x * sin,
        y: point.y * cos + normal.y * sin,
        z: point.z * cos + normal.z * sin,
    };
};
/**
 * The point a fraction of the way along the great circle between two airports, before any bow.
 *
 * Slerp rather than a latitude/longitude lerp: a linear interpolation of the two scalars
 * cuts the corner on anything long-haul and would draw a flight from İzmir to Beijing
 * through the Caspian. Two coincident airports have no circle to walk, so they stay put.
 */
function greatCirclePoint(from, to, t) {
    const lat1 = toRadians(from.lat);
    const lng1 = toRadians(from.lng);
    const lat2 = toRadians(to.lat);
    const lng2 = toRadians(to.lng);
    const dLat = lat2 - lat1;
    const dLng = lng2 - lng1;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    const angular = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
    if (angular === 0)
        return { lat: from.lat, lng: from.lng };
    const left = Math.sin((1 - t) * angular) / Math.sin(angular);
    const right = Math.sin(t * angular) / Math.sin(angular);
    const x = left * Math.cos(lat1) * Math.cos(lng1) + right * Math.cos(lat2) * Math.cos(lng2);
    const y = left * Math.cos(lat1) * Math.sin(lng1) + right * Math.cos(lat2) * Math.sin(lng2);
    const z = left * Math.sin(lat1) + right * Math.sin(lat2);
    const latitude = toDegrees(Math.atan2(z, Math.sqrt(x * x + y * y)));
    const longitude = toDegrees(Math.atan2(y, x));
    return { lat: latitude, lng: ((longitude + 540) % 360) - 180 };
}
/**
 * The normal of the great circle's own plane — the direction a route bows toward.
 *
 * Both endpoints stay exactly put under the bow, and two bows of opposite sign fan one bundle of
 * near-parallel routes apart. A route flown the other way round gets the opposite normal, so the
 * caller stamps the lift's sign per direction to keep one city pair on one line.
 * `null` where there is no plane to bow out of: two coincident or antipodal endpoints.
 */
function bowNormal(from, to) {
    const a = unitVector(toRadians(from.lat), toRadians(from.lng));
    const b = unitVector(toRadians(to.lat), toRadians(to.lng));
    const x = a.y * b.z - a.z * b.y;
    const y = a.z * b.x - a.x * b.z;
    const z = a.x * b.y - a.y * b.x;
    const length = Math.sqrt(x * x + y * y + z * z);
    if (length < BOW_AXIS_MIN_LENGTH)
        return null;
    return { x: x / length, y: y / length, z: z / length };
}
/**
 * A point on the bow: the great circle sample tilted `liftRadians * sin(pi t)` radians out of the
 * circle's own plane, toward its normal. Zero at both ends and widest at mid-route, so the route
 * still leaves and reaches its airports exactly where it always did.
 */
function bowedPoint(from, to, t, normal, liftRadians) {
    const at = greatCirclePoint(from, to, t);
    if (t <= 0 || t >= 1)
        return at;
    const delta = liftRadians * Math.sin(Math.PI * t);
    const here = unitVector(toRadians(at.lat), toRadians(at.lng));
    return pointOf(outOfPlane(here, normal, delta));
}
/**
 * The point a fraction of the way along the great circle between two airports.
 *
 * Slerp rather than a latitude/longitude lerp: a linear interpolation of the two scalars
 * cuts the corner on anything long-haul and would draw a flight from İzmir to Beijing
 * through the Caspian. `progress` outside 0..1 is clamped, because a plane does not
 * overshoot its destination just because the clock ran on.
 *
 * `liftRadians` bows the route out of its own plane — a drawn flight path rather than the
 * great circle's near-straight line — and the heading then follows the bow, because a nose
 * that points along the chord while the route curves beside it reads as a broken aircraft.
 * The default 0 returns exactly the great-circle point and course this function has always
 * returned, which is what the dashboard map and every stored leg were drawn from.
 */
export function interpolateLeg(from, to, progress, liftRadians = 0) {
    const t = Math.min(1, Math.max(0, Number.isFinite(progress) ? progress : 0));
    const bearingDegrees = legBearingDegrees(from, to);
    const normal = liftRadians === 0 ? null : bowNormal(from, to);
    if (normal === null) {
        const at = greatCirclePoint(from, to, t);
        return { latitude: at.lat, longitude: at.lng, bearingDegrees };
    }
    const at = bowedPoint(from, to, t, normal, liftRadians);
    const before = bowedPoint(from, to, Math.max(0, t - BOW_TANGENT_STEP), normal, liftRadians);
    const after = bowedPoint(from, to, Math.min(1, t + BOW_TANGENT_STEP), normal, liftRadians);
    return {
        latitude: at.lat,
        longitude: at.lng,
        bearingDegrees: legBearingDegrees(before, after),
    };
}
/**
 * The route as a map must draw it: the same geometry the aircraft is placed on, sampled.
 *
 * A two-point line handed to a map is drawn as a straight chord in that map's own projection,
 * which leaves an aircraft interpolated along the great circle tens of kilometres off the line
 * that claims to be its route (İzmir to Frankfurt: ~40 km at the midpoint). Sampling the very
 * geometry `interpolateLeg` places the aircraft on — the same bow, about the same axis — makes
 * the line and the aircraft agree by construction rather than by two implementations happening
 * to match. The samples carry no heading, so nothing here pays for one.
 */
export function legPath(from, to, segments = 64, liftRadians = 0) {
    const steps = Math.max(1, Math.floor(segments));
    const normal = liftRadians === 0 ? null : bowNormal(from, to);
    return Array.from({ length: steps + 1 }, (_, index) => {
        const t = index / steps;
        if (normal === null)
            return greatCirclePoint(from, to, t);
        return bowedPoint(from, to, t, normal, liftRadians);
    });
}
//# sourceMappingURL=flight-geo.js.map